/**
 * Shared mastery helpers for Connections sidebar filter (Phase 7).
 * Matches WorkspaceDetail's calculateBasketMastery; Poor/High/Full buckets.
 * Locked template baskets are excluded for free users (optional premium opts).
 */

import { isLocked } from '../../lib/premiumLock';

export const MASTERY_FILTERS = {
  POOR: 'poor',
  HIGH: 'high',
  FULL: 'full',
};

export const MASTERY_FILTER_OPTIONS = [
  { id: MASTERY_FILTERS.POOR, label: 'Poor' },
  { id: MASTERY_FILTERS.HIGH, label: 'High' },
  { id: MASTERY_FILTERS.FULL, label: 'Full' },
];

/** All-or-nothing percentage of ripe topics in a basket (0 if empty). */
export const calculateBasketMastery = (basket) => {
  if (!basket?.topics?.length) return 0;
  const masteredTopics = basket.topics.filter((topic) => topic.is_ripe).length;
  return (masteredTopics / basket.topics.length) * 100;
};

/**
 * @param {object} basket
 * @param {string|null} filter
 * @param {{ isPremium?: () => boolean, isLoading?: boolean }} [opts]
 */
export const basketMatchesMasteryFilter = (basket, filter, opts = {}) => {
  const { isPremium, isLoading } = opts;
  if (
    typeof isPremium === 'function' &&
    isLocked(basket, isPremium, isLoading)
  ) {
    return false;
  }

  if (!filter) return true;

  const mastery = calculateBasketMastery(basket);

  switch (filter) {
    case MASTERY_FILTERS.POOR:
      return mastery < 50;
    case MASTERY_FILTERS.HIGH:
      return mastery >= 50 && mastery < 100;
    case MASTERY_FILTERS.FULL:
      return mastery === 100;
    default:
      return true;
  }
};

export const filterBasketsByMastery = (baskets, filter, opts = {}) => {
  if (!filter) {
    // Still exclude locked baskets from "all filtered" when a filter is off?
    // Caller only uses this when filter is set; when null ConnectionsPage passes
    // unfiltered list. Keep: if no filter, return all (locked still visible in grid).
    return baskets || [];
  }
  return (baskets || []).filter((basket) =>
    basketMatchesMasteryFilter(basket, filter, opts)
  );
};

/** Counts per mastery bucket; must match filterBasketsByMastery / basketMatchesMasteryFilter. */
export const countBasketsByMastery = (baskets, opts = {}) => {
  const list = baskets || [];
  return {
    poor: list.filter((b) =>
      basketMatchesMasteryFilter(b, MASTERY_FILTERS.POOR, opts)
    ).length,
    high: list.filter((b) =>
      basketMatchesMasteryFilter(b, MASTERY_FILTERS.HIGH, opts)
    ).length,
    full: list.filter((b) =>
      basketMatchesMasteryFilter(b, MASTERY_FILTERS.FULL, opts)
    ).length,
  };
};
